import { StoreRtoFlags } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { EditHolidayEventScreenProps } from 'home/stack/types'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { showHeaderTitle } from 'shared/header/header_title'
import { useStores } from 'stores/queries/list'
import { useHolidayEvents } from './queries/list'
import { useUpdateHolidayEvent } from './queries/update'
import { EditHolidayEventView } from './views/edit_holiday_event_view'

export function EditHolidayEventScreen({
  navigation,
  route,
}: EditHolidayEventScreenProps) {
  const { theme } = useTheme()
  const { data: stores } = useStores(data => data.stores)

  const holidayEventId = route.params.holidayEventId
  const holidayEventQuery = useHolidayEvents({ id: holidayEventId })
  const editHolidayEventMutation = useUpdateHolidayEvent()
  const holidayEvent = holidayEventQuery.data

  const selectedStores = stores?.filter(store =>
    holidayEvent?.storeRtoFlags?.some(
      flag => flag.storeId === store.id && flag.blockRto
    )
  )

  const [storesRtoFlags, setStoresRtoFlags] = useState<StoreRtoFlags[]>([])

  useEffect(() => {
    if (stores) {
      const initialStoresRtoFlags: StoreRtoFlags[] = stores.map(store => {
        const isSelected =
          holidayEvent?.storeRtoFlags?.some(
            flag => flag.storeId === store.id && flag.blockRto
          ) || false

        return {
          storeId: store.id,
          blockRto: isSelected,
        }
      })

      setStoresRtoFlags(initialStoresRtoFlags)
    }
  }, [stores, holidayEvent])

  const [loading, setLoading] = useState<boolean>(false)

  const onSave = useCallback(async () => {
    if (!holidayEvent) return
    setLoading(true)
    try {
      await editHolidayEventMutation.mutateAsync({
        id: holidayEventId,
        storeRtoFlags: storesRtoFlags,
      })
      navigation.goBack()
    } catch (error) {
      console.error(error)
      errorAlert(error, t('editHolidayEventErrorTitle'))
    } finally {
      setLoading(false)
    }
  }, [
    editHolidayEventMutation,
    holidayEvent,
    holidayEventId,
    navigation,
    storesRtoFlags,
  ])

  useLayoutEffect(() => {
    showHeaderTitle(navigation, t('editHolidayEventTitle'))
    showHeaderButton({
      navigation,
      theme,
      title: t('save'),
      onPress: onSave,
      position: 'right',
      disabled: false,
    })
  }, [navigation, onSave, theme])

  return (
    <StatusBoundary
      isLoading={holidayEventQuery.isLoading || loading}
      error={holidayEventQuery.error}
    >
      <EditHolidayEventView
        stores={stores}
        holidayEvent={holidayEventQuery.data}
        selectedStores={selectedStores}
        onChangeStoresRtoFlags={setStoresRtoFlags}
        storesRtoFlags={storesRtoFlags}
      />
    </StatusBoundary>
  )
}
